// src/App.js (Updated Version with Domain-Based Routing)
// cSpell:ignore firestore sommel analytics
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { auth } from "./firebase-config";
import { onAuthStateChanged } from "firebase/auth";
import { RestaurantProvider } from "./contexts/RestaurantContext";
import PublicMenu from "./PublicMenu";
import LoginPage from "./LoginPage";
import AdminDashboard from "./AdminDashboard";
import DrinkDetailPage from "./DrinkDetailPage";
import LandingPage from "./LandingPage";
import RestaurantLanding from "./components/RestaurantLanding";
import IntegratedEnhancementDemo from "./components/IntegratedEnhancementDemo";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import PairingDemo from "./components/PairingDemo";
import V5StandaloneDemo from "./components/V5StandaloneDemo";
import Phase2AIFeaturesComponent from "./components/Phase2AIFeaturesComponent";
import EnhancementTestPage from "./components/EnhancementTestPage";
// Phase 2.2: Intelligent Pairing System UI Components
import PairingInterface from "./components/PairingInterface";
import PairingExplorer from "./components/PairingExplorer";
import PairingAnalytics from "./components/PairingAnalytics";
import PairingFeedback from "./components/PairingFeedback";
import Phase2PairingDemo from "./components/Phase2PairingDemo";
// Phase 2.3: Advanced Search & Recommendation Engine UI Components
import AdvancedSearchInterface from "./components/AdvancedSearchInterface";
import RecommendationEngine from "./components/RecommendationEngine";
// Beer Pairing Interface
import BeerPairingInterface from "./components/BeerPairingInterface";
// Phase 4: Enhanced User Experience Dashboard
import Phase4Dashboard from "./components/Phase4Dashboard";
// Phase 5: Advanced AI Features Components
import Phase5Dashboard from "./components/Phase5Dashboard";
import MLInsights from "./components/MLInsights";
import PredictiveAnalytics from "./components/PredictiveAnalytics";
// This component is now aware of the loading state
const ProtectedRoute = ({ user, loading }) => {
  if (loading) {
    // Only show "Loading..." while the initial check is running
    return <div>Loading...</div>;
  }
  // Once the check is complete, either show the content or redirect
  return user ? <Outlet /> : <Navigate to="/login" />;
};

ProtectedRoute.propTypes = {
  user: PropTypes.object,
  loading: PropTypes.bool.isRequired,
};

// Component to determine what to show at the root based on domain
const RootRoute = () => {
  const isLocalhost = useMemo(() => {
    const hostname = window.location.hostname;
    return hostname === "localhost" || hostname === "127.0.0.1";
  }, []);

  if (isLocalhost) {
    // On localhost, show Lucky Dog Tavern menu for owner testing
    return <LandingPage />;
  } else {
    // On production domain (ldtmenu.com), show SommelAI sales page
    return <RestaurantLanding />;
  }
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <Router>
      <RestaurantProvider>
        <Routes>
          {/* Domain-based root route */}
          <Route path="/" element={<RootRoute />} />

          {/* Authentication routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Demo routes */}
          <Route
            path="/demo/enhancement"
            element={<IntegratedEnhancementDemo />}
          />
          <Route path="/demo/analytics" element={<AnalyticsDashboard />} />
          <Route path="/demo/pairing" element={<PairingDemo />} />
          <Route path="/demo/v5" element={<V5StandaloneDemo />} />
          <Route path="/demo/phase2" element={<Phase2AIFeaturesComponent />} />
          <Route
            path="/demo/enhancement-test"
            element={<EnhancementTestPage />}
          />

          {/* Phase 2.2: Intelligent Pairing System UI Routes */}
          <Route path="/pairing" element={<PairingInterface />} />
          <Route path="/pairing/explorer" element={<PairingExplorer />} />
          <Route path="/pairing/analytics" element={<PairingAnalytics />} />
          <Route path="/pairing/feedback" element={<PairingFeedback />} />
          <Route path="/demo/phase2-pairing" element={<Phase2PairingDemo />} />

          {/* Beer Pairing Interface Route */}
          <Route path="/beer-pairing" element={<BeerPairingInterface />} />

          {/* Phase 2.3: Advanced Search & Recommendation Engine Routes */}
          <Route path="/search" element={<AdvancedSearchInterface />} />
          <Route path="/recommendations" element={<RecommendationEngine />} />
          <Route
            path="/demo/phase2.3"
            element={
              <div style={{ padding: "2rem" }}>
                <h2>Phase 2.3: Advanced Search & Recommendation Engine Demo</h2>
                <AdvancedSearchInterface />
                <div style={{ marginTop: "3rem" }}>
                  <RecommendationEngine />
                </div>
              </div>
            }
          />

          {/* Multi-tenant pairing routes */}
          <Route
            path="/restaurant/:restaurantSlug/pairing"
            element={<PairingInterface />}
          />
          <Route
            path="/restaurant/:restaurantSlug/pairing/explorer"
            element={<PairingExplorer />}
          />
          <Route
            path="/restaurant/:restaurantSlug/pairing/analytics"
            element={<PairingAnalytics />}
          />
          <Route
            path="/restaurant/:restaurantSlug/pairing/feedback"
            element={<PairingFeedback />}
          />

          {/* Multi-tenant search and recommendation routes */}
          <Route
            path="/restaurant/:restaurantSlug/search"
            element={<AdvancedSearchInterface />}
          />
          <Route
            path="/restaurant/:restaurantSlug/recommendations"
            element={<RecommendationEngine />}
          />

          {/* Legacy routes for backward compatibility */}
          <Route path="/menu" element={<PublicMenu />} />
          <Route path="/drinks" element={<PublicMenu />} />

          {/* Multi-tenant routes */}
          <Route
            path="/restaurant/:restaurantSlug/menu"
            element={<PublicMenu />}
          />
          <Route
            path="/restaurant/:restaurantSlug/drinks"
            element={<PublicMenu />}
          />
          <Route path="/restaurant/:restaurantSlug" element={<LandingPage />} />

          {/* Public route for drink details - accessible to all users */}
          <Route path="/drink/:id" element={<DrinkDetailPage />} />
          <Route
            path="/restaurant/:restaurantSlug/drink/:id"
            element={<DrinkDetailPage />}
          />

          {/* Protected routes */}
          <Route
            element={<ProtectedRoute user={currentUser} loading={loading} />}
          >
            <Route path="/admin" element={<AdminDashboard />} />
            <Route
              path="/restaurant/:restaurantSlug/admin"
              element={<AdminDashboard />}
            />
            <Route path="/phase4" element={<Phase4Dashboard />} />
            <Route
              path="/restaurant/:restaurantSlug/phase4"
              element={<Phase4Dashboard />}
            />
            <Route path="/phase5" element={<Phase5Dashboard />} />
            <Route
              path="/restaurant/:restaurantSlug/phase5"
              element={<Phase5Dashboard />}
            />
            <Route path="/ml-insights" element={<MLInsights />} />
            <Route
              path="/restaurant/:restaurantSlug/ml-insights"
              element={<MLInsights />}
            />
            <Route
              path="/predictive-analytics"
              element={<PredictiveAnalytics />}
            />
            <Route
              path="/restaurant/:restaurantSlug/predictive-analytics"
              element={<PredictiveAnalytics />}
            />
          </Route>
        </Routes>
      </RestaurantProvider>
    </Router>
  );
}

export default App;
